import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * InvoicesListPage - List and manage all invoices
 * Allows users to view, search, and edit older invoices
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Icon, type IconName } from '../components/ui/Icon';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { FormField } from '../components/forms/FormField';
import { SelectField } from '../components/forms/SelectField';
import {
  useUnifiedTransactions,
  useTransactionOperations,
} from '../hooks/useApi';
import { UnifiedTransaction, ServiceType } from '../types/api';
import { SERVICE_TYPE_OPTIONS } from '../types/forms';
import { formatDate, formatServiceType } from '../utils/formatters';
import {
  canDeleteTransaction,
  getTransactionTypeLabel as getTransactionLabel,
  isSalesTransaction,
} from '../utils/transactions';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { toast } from 'react-hot-toast';
import {
  InvoiceFilters,
  buildUnifiedTransactionFilters,
} from './InvoicesListPage.filters';

// ============================================================================
// Types
// ============================================================================

type InvoicesListPageProps = Record<string, never>;

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const FiltersSection = styled.div`
  background: var(--surface);
  padding: var(--space-lg);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  margin-bottom: var(--space-xl);
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: var(--space-md);
  align-items: end;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

// Table-based layout for transactions
const TransactionTable = styled.div`
  min-width: 0;
  width: 100%;
  overflow-x: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  @media (max-width: 767px) {
    display: none;
  }
`;

const TableHeader = styled.div.attrs({ role: 'row' })`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr 1fr auto;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  background: var(--surface-subtle);
  border-bottom: 1px solid var(--border);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  text-transform: none;
  letter-spacing: 0.05em;

  min-width: 680px;
`;

const TableRow = styled.div.attrs({ role: 'row' })`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr 1fr auto;
  gap: var(--space-md);
  padding: var(--space-lg);
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--surface-subtle);
  }

  min-width: 680px;
`;

const DateCell = styled.div.attrs({ role: 'cell' })`
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
`;

const TypeCell = styled.div.attrs({ role: 'cell' })``;

const PeriodCell = styled.div.attrs({ role: 'cell' })`
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
`;

const ValueCell = styled.div.attrs({ role: 'cell' })`
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
`;

const ActionCell = styled.div.attrs({ role: 'cell' })``;

const UnitReviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  margin-top: var(--space-xs);
  padding: 2px var(--space-xs);
  border: 1px solid var(--warning-500);
  border-radius: 4px;
  background: var(--warning-100);
  color: var(--text-primary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
`;

function UnitReviewFlag() {
  return (
    <UnitReviewBadge title="Unidad no válida: este registro no se incluye en los totales. Ábrelo para corregir la unidad.">
      <Icon name="warning" size="xs" aria-hidden />
      Revisar unidad
      <span className="sr-only">
        : no se incluye en los totales hasta corregir la unidad
      </span>
    </UnitReviewBadge>
  );
}

// Mobile layout for responsiveness
const MobileList = styled.div`
  display: none;
  @media (max-width: 767px) {
    display: block;
  }
`;

const MobileCard = styled(Card)`
  padding: var(--space-md);
  margin-bottom: var(--space-md);
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-sm);
`;

const MobileTitle = styled.div`
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
`;

const MobileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  margin-bottom: var(--space-sm);
`;

const MobileDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);

  .label {
    color: var(--text-muted);
  }

  .value {
    color: var(--text-primary);
    font-weight: var(--font-weight-medium);
  }
`;

// Transaction Type Status Badges
const TransactionTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  line-height: 1;
`;

const ActionButton = styled(Button)<{ $danger?: boolean }>`
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  height: auto;
  min-height: 2rem;

  ${(props) =>
    props.$danger &&
    css`
      background-color: var(--danger-600);
      border-color: var(--danger-600);
      color: #ffffff;

      &:hover {
        background-color: var(--danger-500);
        border-color: var(--danger-500);
      }

      &:focus-visible {
        outline: 3px solid var(--danger-100);
        outline-offset: 2px;
      }
    `}

  &:hover {
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--space-3xl);
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-lg);
  opacity: 0.6;
`;

const EmptyMessage = styled.p`
  margin: 0 0 var(--space-xl) 0;
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
`;

const LoadingContainer = styled.div`
  padding: var(--space-3xl);
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: var(--space-3xl);
  color: var(--danger-600);
`;

// ============================================================================
// Helper Functions
// ============================================================================

const getTypeDisplayLabel = (transaction: UnifiedTransaction): string => {
  if (isSalesTransaction(transaction.transaction_type)) {
    return 'Cubiertos';
  }

  if (transaction.service_type) {
    return formatServiceType(transaction.service_type);
  }

  return 'Servicio';
};

const SERVICE_TYPE_ICON_MAP: Record<ServiceType, IconName> = {
  electricidad: 'electricity',
  agua: 'water',
  gas: 'gas',
  'residuos-reciclables': 'waste',
  'residuos-organicos': 'eco',
  'residuos-generales': 'waste',
};

const getTypeIconName = (transaction: UnifiedTransaction): IconName => {
  if (isSalesTransaction(transaction.transaction_type)) {
    return 'users';
  }

  if (transaction.service_type) {
    return SERVICE_TYPE_ICON_MAP[transaction.service_type];
  }

  if (transaction.transaction_type === 'ocr') {
    return 'image';
  }

  if (transaction.transaction_type === 'manual') {
    return 'edit';
  }

  return 'file';
};

// ============================================================================
// Component
// ============================================================================

export const InvoicesListPage: React.FC<InvoicesListPageProps> = () => {
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState<InvoiceFilters>({
    transaction_type: '',
    service_type: '',
    date_from: '',
    date_to: '',
  });

  // Delete functionality state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    transaction: UnifiedTransaction | null;
  }>({ isOpen: false, transaction: null });

  // Transaction operations
  const { deleteTransaction, isLoading: isDeleting } =
    useTransactionOperations();

  // Prepare API filters
  const apiFilters = useMemo(() => {
    const params = buildUnifiedTransactionFilters(filters);

    console.log('🔍 [API Filters] Generated filters for API call:', params);
    return params;
  }, [filters]);

  // Fetch unified transactions
  const {
    data: transactionsResponse,
    isLoading,
    error,
    refetch,
  } = useUnifiedTransactions(apiFilters);

  const transactions = transactionsResponse?.items || [];

  // Debug current filters
  console.log('📊 [Current State] filters:', filters);
  console.log('📊 [Current State] apiFilters:', apiFilters);
  console.log('📊 [Current State] transactions count:', transactions.length);

  // Handle filter changes
  const handleFilterChange = (field: keyof InvoiceFilters, value: string) => {
    console.log(`🎯 [Filter Change] ${field}:`, value);
    setFilters((prev) => {
      const isSalesType = value === 'sales_ocr' || value === 'sales_manual';
      const shouldResetService =
        field === 'transaction_type' && isSalesType && prev.service_type;

      const updatedFilters: InvoiceFilters = {
        ...prev,
        [field]: value,
        ...(shouldResetService ? { service_type: '' } : {}),
      };

      console.log('🔄 [Filter Update] Previous:', prev);
      console.log('✅ [Filter Update] New:', updatedFilters);

      return updatedFilters;
    });
  };

  // Handle transaction click - route covers transactions to SalesResultsPage, others to InvoiceResultsPage
  const handleTransactionClick = (transaction: UnifiedTransaction) => {
    // Route covers transactions (sales_ocr and sales_manual) to SalesResultsPage
    if (
      transaction.transaction_type === 'sales_ocr' ||
      transaction.transaction_type === 'sales_manual'
    ) {
      navigate(`/sales-results/${transaction.id}`);
    } else {
      // Route service transactions to InvoiceResultsPage
      navigate(
        `/transaction-details?id=${transaction.id}&type=${transaction.transaction_type}&origin=transactions`,
      );
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      transaction_type: '',
      service_type: '',
      date_from: '',
      date_to: '',
    });
  };

  // Handle delete modal
  const handleDeleteClick = (transaction: UnifiedTransaction) => {
    setDeleteModal({ isOpen: true, transaction });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, transaction: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.transaction) return;

    try {
      console.log(
        '🗑️ [InvoicesListPage] Deleting transaction:',
        deleteModal.transaction,
      );

      await deleteTransaction({
        id: deleteModal.transaction.id,
        transactionType: deleteModal.transaction.transaction_type as
          | 'ocr'
          | 'manual'
          | 'sales_ocr'
          | 'sales_manual',
      });

      const transactionLabel = getTransactionLabel(
        deleteModal.transaction.transaction_type,
      );
      toast.success(`${transactionLabel} eliminada correctamente`);

      // Close modal and refresh data
      setDeleteModal({ isOpen: false, transaction: null });
      refetch();
    } catch (error: any) {
      console.error('❌ [InvoicesListPage] Error deleting transaction:', error);

      // Provide specific error messages
      let errorMessage = 'Error al eliminar la transacción';
      if (error?.response?.status === 404) {
        errorMessage = 'La transacción no existe o ya ha sido eliminada';
      } else if (error?.response?.status === 403) {
        errorMessage = 'No tienes permisos para eliminar esta transacción';
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>Actividad</Title>
        <Subtitle>
          Gestiona y audita todas tus entradas de datos. Filtra por tipo de
          transacción, tipo de servicio y fecha de creación.
        </Subtitle>
      </Header>

      {/* Filters Section */}
      <FiltersSection>
        <FiltersGrid>
          <SelectField
            name="transaction_type"
            label="Tipo de Transacción"
            placeholder="Todas las transacciones"
            options={[
              { value: '', label: 'Todas las transacciones' },
              { value: 'sales_ocr', label: 'Ventas (OCR)' },
              { value: 'sales_manual', label: 'Ventas (Manual)' },
              { value: 'ocr', label: 'Servicios (OCR)' },
              { value: 'manual', label: 'Servicios (Manual)' },
            ]}
            value={filters.transaction_type}
            onChange={(value) => {
              console.log(
                '🔽 [SelectField] Transaction type onChange called with:',
                value,
                typeof value,
              );
              handleFilterChange('transaction_type', value as string);
            }}
          />

          <SelectField
            name="service_type"
            label="Tipo de Servicio"
            placeholder="Todos los servicios"
            options={[
              { value: '', label: 'Todos los servicios' },
              ...SERVICE_TYPE_OPTIONS,
            ]}
            value={filters.service_type}
            onChange={(value) => {
              console.log(
                '🔽 [SelectField] Service type onChange called with:',
                value,
                typeof value,
              );
              handleFilterChange('service_type', value as string);
            }}
            disabled={
              filters.transaction_type === 'sales_ocr' ||
              filters.transaction_type === 'sales_manual'
            }
          />

          <FormField
            name="date_from"
            type="date"
            label="Desde"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
          />

          <FormField
            name="date_to"
            type="date"
            label="Hasta"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
          />
        </FiltersGrid>

        {(filters.transaction_type ||
          filters.service_type ||
          filters.date_from ||
          filters.date_to) && (
          <div style={{ marginTop: 'var(--space-md)' }}>
            <Button variant="secondary" size="sm" onClick={handleClearFilters}>
              Limpiar filtros
            </Button>
          </div>
        )}
      </FiltersSection>

      {/* Content */}
      {isLoading ? (
        <LoadingContainer>
          <LoadingSpinner message="Cargando actividad…" />
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <EmptyIcon>
            <Icon name="warning" size="lg" aria-hidden />
          </EmptyIcon>
          <EmptyMessage>No se pudo cargar la actividad</EmptyMessage>
          <Button variant="primary" onClick={() => refetch()}>
            Reintentar
          </Button>
        </ErrorContainer>
      ) : transactions.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Icon name="file" size="lg" aria-hidden />
          </EmptyIcon>
          <EmptyMessage>
            {Object.keys(apiFilters).length > 0
              ? 'No se encontraron transacciones con los filtros aplicados'
              : 'No tienes transacciones procesadas aún'}
          </EmptyMessage>
          <Button variant="primary" onClick={() => navigate('/upload')}>
            Añadir datos
          </Button>
        </EmptyState>
      ) : (
        <>
          {/* Desktop Table Layout */}
          <TransactionTable role="table" aria-label="Actividad" tabIndex={0}>
            <TableHeader>
              <div role="columnheader">Fecha Registro</div>
              <div role="columnheader">Tipo</div>
              <div role="columnheader">Periodo</div>
              <div role="columnheader">Valor</div>
              <div role="columnheader">Acción</div>
            </TableHeader>

            {transactions.map((transaction) => {
              const canEdit = true; // All transactions can now be viewed

              return (
                <TableRow
                  key={`${transaction.transaction_type}-${transaction.id}`}
                >
                  <DateCell>
                    {formatDate(
                      transaction.created_at || transaction.start_date,
                    )}
                  </DateCell>

                  <TypeCell>
                    <TransactionTypeBadge>
                      <Icon
                        name={getTypeIconName(transaction)}
                        size="xs"
                        aria-hidden
                      />
                      {getTypeDisplayLabel(transaction)}
                    </TransactionTypeBadge>
                  </TypeCell>

                  <PeriodCell>
                    {transaction.start_date && transaction.end_date
                      ? `${formatDate(transaction.start_date)} - ${formatDate(transaction.end_date)}`
                      : formatDate(
                          transaction.start_date || transaction.created_at,
                        )}
                  </PeriodCell>

                  <ValueCell>
                    {transaction.value_display}
                    {transaction.unit_needs_review && (
                      <div>
                        <UnitReviewFlag />
                      </div>
                    )}
                  </ValueCell>

                  <ActionCell>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
                      {canEdit ? (
                        <ActionButton
                          variant="secondary"
                          size="sm"
                          onClick={() => handleTransactionClick(transaction)}
                          aria-label={`Ver detalles de transacción ${transaction.id}`}
                        >
                          <Icon name="eye" size="xs" />
                          Ver
                        </ActionButton>
                      ) : (
                        <ActionButton
                          variant="secondary"
                          size="sm"
                          disabled
                          aria-label="Transacción de solo lectura"
                        >
                          <Icon name="eye" size="xs" />
                          Ver
                        </ActionButton>
                      )}

                      {canDeleteTransaction(transaction.transaction_type) && (
                        <ActionButton
                          $danger
                          size="sm"
                          onClick={() => handleDeleteClick(transaction)}
                          aria-label={`Eliminar transacción ${transaction.id}`}
                        >
                          <Icon name="delete" size="xs" />
                        </ActionButton>
                      )}
                    </div>
                  </ActionCell>
                </TableRow>
              );
            })}
          </TransactionTable>

          {/* Mobile Card Layout */}
          <MobileList>
            {transactions.map((transaction) => {
              const canEdit = true; // All transactions can now be viewed

              return (
                <MobileCard
                  key={`mobile-${transaction.transaction_type}-${transaction.id}`}
                >
                  <MobileHeader>
                    <MobileTitle>
                      {transaction.start_date && transaction.end_date
                        ? `${formatDate(transaction.start_date)} - ${formatDate(transaction.end_date)}`
                        : formatDate(
                            transaction.start_date || transaction.created_at,
                          )}
                    </MobileTitle>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
                      {canEdit ? (
                        <ActionButton
                          variant="secondary"
                          size="sm"
                          onClick={() => handleTransactionClick(transaction)}
                        >
                          Ver
                        </ActionButton>
                      ) : (
                        <ActionButton variant="secondary" size="sm" disabled>
                          Ver
                        </ActionButton>
                      )}

                      {canDeleteTransaction(transaction.transaction_type) && (
                        <ActionButton
                          $danger
                          size="sm"
                          aria-label={`Eliminar transacción ${transaction.id}`}
                          onClick={() => handleDeleteClick(transaction)}
                        >
                          <Icon name="delete" size="xs" />
                        </ActionButton>
                      )}
                    </div>
                  </MobileHeader>

                  <MobileDetails>
                    <MobileDetail>
                      <span className="label">Fecha Registro:</span>
                      <span className="value">
                        {formatDate(
                          transaction.created_at || transaction.start_date,
                        )}
                      </span>
                    </MobileDetail>

                    <MobileDetail>
                      <span className="label">Tipo:</span>
                      <TransactionTypeBadge>
                        <Icon
                          name={getTypeIconName(transaction)}
                          size="xs"
                          aria-hidden
                        />
                        {getTypeDisplayLabel(transaction)}
                      </TransactionTypeBadge>
                    </MobileDetail>

                    <MobileDetail>
                      <span className="label">Periodo:</span>
                      <span className="value">
                        {transaction.start_date && transaction.end_date
                          ? `${formatDate(transaction.start_date)} - ${formatDate(transaction.end_date)}`
                          : formatDate(
                              transaction.start_date || transaction.created_at,
                            )}
                      </span>
                    </MobileDetail>

                    <MobileDetail>
                      <span className="label">Valor:</span>
                      <span className="value">
                        {transaction.value_display}
                        {transaction.unit_needs_review && <UnitReviewFlag />}
                      </span>
                    </MobileDetail>
                  </MobileDetails>
                </MobileCard>
              );
            })}
          </MobileList>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Transacción"
        message={
          deleteModal.transaction
            ? `¿Estás seguro de que quieres eliminar esta ${getTransactionLabel(
                deleteModal.transaction.transaction_type,
              ).toLowerCase()}? Esta acción no se puede deshacer.`
            : 'Esta acción no se puede deshacer.'
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={isDeleting}
        variant="danger"
      />
    </PageContainer>
  );
};

export default InvoicesListPage;
